import React from 'react'
import { TrendingUp, Users, Zap, Shield, DollarSign, Clock, Target, Server, Brain, Bot } from 'lucide-react'

const Impact = () => {
  const keyAchievements = [
    {
      icon: Server,
      title: 'Cloud-Native Fintech Platform',
      description: 'Shipped end-to-end Nuff.Cash on $0/month infrastructure — dual Spring Boot services, TiDB Cloud distributed DB, Oracle Cloud VMs — from architecture through live production.',
      highlight: '$0/mo Infrastructure',
    },
    {
      icon: Bot,
      title: 'Autonomous AI Pipeline',
      description: 'Built SoloForge autonomous COO: 4-agent CrewAI pipeline with AI-native Skyfire payments, Snowflake Cortex ML forecasting, and complete lead-to-revenue lifecycle.',
      highlight: '4-Agent System',
    },
    {
      icon: Shield,
      title: 'AI Fraud Detection',
      description: 'Delivered production AI fraud detection achieving 35% fraud reduction and 94% classification accuracy on 1M+ monthly transactions with sub-100ms latency.',
      highlight: '35% Fraud Reduction',
    },
    {
      icon: Brain,
      title: 'ML Model Optimization',
      description: 'Improved ML model accuracy 22% through structured feedback loops, A/B testing, and continuous learning pipelines with automated drift detection.',
      highlight: '22% Accuracy Boost',
    },
    {
      icon: Zap,
      title: 'Apple B2C AI Integration',
      description: 'Architected Apple B2C AI integration improving data processing speed 50% and customer engagement 22% through NLP pipelines and AI-driven personalization.',
      highlight: '50% Speed Improvement',
    },
    {
      icon: Users,
      title: 'Engineering Leadership',
      description: 'Led 30+ engineer organizations establishing AI-centric SDLC standards, model governance, compliance frameworks, and engineering excellence across multiple companies.',
      highlight: '30+ Engineers',
    },
  ]

  const impactMetrics = [
    { value: '1M+', label: 'Monthly Transactions', icon: TrendingUp },
    { value: '99.99%', label: 'System Uptime', icon: Server },
    { value: '35%', label: 'Fraud Reduction', icon: Shield },
    { value: '94%', label: 'ML Accuracy', icon: Target },
    { value: '50%', label: 'Speed Improvement', icon: Zap },
    { value: '30%', label: 'Validation Cycles Reduced', icon: Clock },
  ]

  const crossFunctionalImpact = [
    { company: 'Luminar Technologies', achievement: 'Reduced validation cycles by 30%', area: 'Autonomous Driving AI' },
    { company: 'LinkedIn', achievement: 'Drove 60% efficiency gains', area: 'Data Governance' },
    { company: 'Xtime', achievement: 'Reduced SaaS downtime by 20%', area: '3,000+ Dealer Integrations' },
  ]
  
  return (
    <section id="impact" className="py-24 relative bg-darker/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
            Key <span className="text-gradient">Achievements</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Measurable results delivered through technical excellence and strategic leadership.
          </p>
        </div>
        
        {/* Impact Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-16">
          {impactMetrics.map((metric, index) => (
            <div key={index} className="glass rounded-xl p-4 text-center hover:border-primary/50 transition-all group">
              <metric.icon className="w-6 h-6 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <div className="text-2xl font-bold text-gradient">{metric.value}</div>
              <div className="text-xs text-gray-400 mt-1">{metric.label}</div>
            </div>
          ))}
        </div>
        
        {/* Key Achievements Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {keyAchievements.map((achievement, index) => (
            <div key={index} className="glass rounded-2xl p-6 hover:border-primary/50 transition-all group">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:from-primary/30 group-hover:to-secondary/30 transition-all">
                  <achievement.icon className="w-6 h-6 text-primary" />
                </div>
                <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                  {achievement.highlight}
                </span>
              </div>
              <h3 className="font-display font-semibold text-white mb-2">{achievement.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{achievement.description}</p>
            </div>
          ))}
        </div>
        
        {/* Cross-Functional TPM Impact */}
        <div className="glass rounded-2xl p-8">
          <h3 className="text-xl font-display font-semibold text-white mb-6 text-center">
            Cross-Functional Program Leadership
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {crossFunctionalImpact.map((item, index) => (
              <div key={index} className="text-center p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <h4 className="font-semibold text-primary mb-2">{item.company}</h4>
                <p className="text-white font-medium mb-1">{item.achievement}</p>
                <p className="text-gray-400 text-sm">{item.area}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Impact
